import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { VideoThumbnail, VideoView, useVideoPlayer } from 'expo-video';
import { TakeoverVolumeButton } from 'features/app-takeover/components/takeover-mute-button';
import { TakeoverReplayButton } from 'features/app-takeover/components/takeover-replay-button';
import { useAppTakeoverSimpleHomePage } from 'features/app-takeover/hooks/use-app-takeover';
import { isNumber } from 'lodash-es';
import LottieView from 'lottie-react-native';
import { LayoutChangeEvent, Pressable, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { withTypography } from 'ucl/components/text/Typography';

import Picture from 'components/picture';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { onboardingIsLikelyCompleteDeferred } from 'state/onboarding/hooks/use-onboarding-content';
import { primitive, spacing } from 'styles/constants';
import { Deferred } from 'utils/Deferred';
import { isAndroid, isCA, isWeb } from 'utils/environment';
import { animatedSplashScreenHiddenDeferred } from 'utils/hide-splash-screen';
import logger from 'utils/logger';
import { useLottieSource } from 'utils/lottie/use-lottie-source';
import { TraceName } from 'utils/performance';
import { stopTrace } from 'utils/performance/trace';

import { CTAButtons } from '../components/cta-button';
import { ISimpleHomePageProps } from '../types';
import { sendHeroClickEvent } from '../utils';

const rgbColorMidpoint = Number('0x777777');

// this only needs to happen once per app load
const onImageLoadedDeferred = new Deferred();
if (__DEV__) {
  onImageLoadedDeferred.resolve(); // live reload throws this off in dev
}

const _SimpleHomePageMobile = ({
  numberOfItemsInCart,
  serviceMode,
  item,
  flatListDimensions,
}: ISimpleHomePageProps) => {
  const [safeAreaHeight, setSafeAreaHeight] = useState<Number | null>(null);
  const appTakeover = useAppTakeoverSimpleHomePage(item._id);
  const [lottieFinished, setLottieFinished] = useState(false);
  const [lottieRenderFailed, setLottieRenderFailed] = useState(false);
  const [videoFinished, setVideoFinished] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoReplayRef = useRef<(() => void) | null>(null);
  const videoMuteRef = useRef<((newMuted: boolean) => void) | null>(null);
  const {
    containerBackgroundColor,
    HeroBottomGradient,
    showReplayButton,
    videoHasAudio, // TODO make this detected by the video player in the future. Right now audio detection is buggy on iOS unfortunately. Maybe SDK 55?
  } = appTakeover;
  const isTakeoverActive = appTakeover.isTakeoverActive;
  const router = useRouter();
  const animationCanPlayRef = useRef(false);
  const handleVideoEnd = useCallback(() => setVideoFinished(true), []);

  // the following is due to a LottieView type mismatch between native and web
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const animationRef = useRef<any>(null);

  const {
    mobileImage,
    mobileImageLottie,
    mobileVideo,
    altImageText,
    _id,
    imageLink,
    mobileImageBackgroundColor,
    imageDisclaimerText,
    daypart,
  } = item;

  const imageAlt = altImageText?.locale ?? '';
  const image = mobileImage?.locale;
  const lottieUrl = mobileImageLottie?.locale?.asset?.url;
  const videoUri = mobileVideo?.locale?.asset?.url;

  const onImageLoaded = useCallback(() => {
    onImageLoadedDeferred.resolve();
  }, []);

  const { source: preparedLottieSource, error: lottiePreparationError } = useLottieSource({
    source: lottieUrl ? { uri: lottieUrl } : undefined,
    surface: 'simple_home_page',
  });
  const hasLottieFailure = Boolean(lottiePreparationError) || lottieRenderFailed;

  useEffect(() => {
    setLottieRenderFailed(false);
  }, [lottieUrl]);

  useEffect(() => {
    if (hasLottieFailure && !image) {
      onImageLoaded();
    }
  }, [hasLottieFailure, image, onImageLoaded]);

  useEffect(() => {
    // web doesn't have these things...
    (isWeb
      ? Promise.resolve()
      : Promise.all([
          onImageLoadedDeferred.promise, // waiting until the image is loaded until playing is required on web or it doesn't work
          animatedSplashScreenHiddenDeferred.promise, // don't play if splash screen is up
          onboardingIsLikelyCompleteDeferred.promise, // don't play if onboarding modal is up
        ])
    ).then(() => {
      stopTrace(TraceName.JAVASCRIPT_START_TO_SIMPLE_HOME_PAGE_LOADED, { shpId: _id });
      stopTrace(TraceName.HOME_LOAD);
      animationCanPlayRef.current = true;
      animationRef?.current?.play();
    });
  }, []);

  // reverse the disclaimer color to white-ish if the background color is dark (below 50% of RGB space)
  const reverseDisclaimerColor =
    mobileImageBackgroundColor &&
    imageDisclaimerText?.locale &&
    Number('0x' + mobileImageBackgroundColor.replace('#', '')) < rgbColorMidpoint;

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;
      // this sets a max height if the safe area of the image is going to get cut off horizontally
      // (this is extremely rare only with devices that are very tall and narrow)
      if (
        width &&
        isNumber(width) &&
        height &&
        isNumber(height) &&
        safeAreaHeight === null &&
        width / height < 0.625
      ) {
        // the aspect ratio of the image safe area is 1.5x the width
        setSafeAreaHeight(width * 1.5);
      }
    },
    [safeAreaHeight]
  );

  const handleOnPress = () => {
    if (imageLink) {
      sendHeroClickEvent({ imageLink, daypart });
      router.navigate(imageLink);
    }
  };

  const safeAreaStyle = safeAreaHeight ? { maxHeight: safeAreaHeight as any } : undefined;

  return (
    <View
      testID="simple-home-page"
      style={{
        height: flatListDimensions?.height,
        ...(isTakeoverActive ? { backgroundColor: containerBackgroundColor } : {}),
      }}
    >
      <View style={styles.heroContainer}>
        <Pressable
          style={[styles.pressable, { backgroundColor: mobileImageBackgroundColor || undefined }]}
          onPress={handleOnPress}
          {...(imageLink ? { accessibilityLabel: imageAlt, role: 'button' } : {})}
        >
          {image && (!lottieUrl || hasLottieFailure) && !videoUri && (
            <Picture
              priority="high"
              image={image}
              alt={imageAlt}
              onDisplay={onImageLoaded}
              style={[styles.image, safeAreaStyle]}
              onLayout={onLayout}
            />
          )}
          {preparedLottieSource && !hasLottieFailure && !videoUri && (
            <View pointerEvents="none" style={[styles.lottie, safeAreaStyle]} onLayout={onLayout}>
              <LottieView
                ref={animationRef}
                resizeMode="cover"
                hardwareAccelerationAndroid
                // handles breakfast->lunch transition if a rerender externally happens so animation plays
                autoPlay={isWeb || animationCanPlayRef.current}
                loop={false}
                style={styles.lottie}
                // the mandatory spec of the lottie is 1536px. on web only hardcoding it to this scales correctly.
                webStyle={{ width: 1536 }}
                onAnimationLoaded={onImageLoaded}
                onAnimationFinish={() => {
                  setLottieFinished(true);
                }}
                onAnimationFailure={() => {
                  setLottieFinished(true);
                  setLottieRenderFailed(true);
                }}
                source={preparedLottieSource}
              />
            </View>
          )}
          {videoUri && (
            <Video
              videoUri={videoUri}
              onLayout={onLayout}
              onLoaded={onImageLoaded}
              safeAreaStyle={safeAreaStyle}
              onVideoEnd={handleVideoEnd}
              onReplayRef={videoReplayRef}
              onMuteRef={videoMuteRef}
            />
          )}
          {imageDisclaimerText?.locale && (
            <DisclaimerText
              style={reverseDisclaimerColor ? { color: primitive.$mayoEggWhite } : undefined}
            >
              {imageDisclaimerText?.locale}
            </DisclaimerText>
          )}
          {isTakeoverActive && !isCA && appTakeover.audio && (
            <TakeoverVolumeButton lottieFinished={lottieFinished} />
          )}
        </Pressable>
        {isTakeoverActive && HeroBottomGradient ? <HeroBottomGradient /> : null}
        {/** these elements have to be here to avoid layout / z-index issues with gradient etc. */}
        {videoUri && (videoHasAudio || (showReplayButton && videoFinished)) && (
          <View style={styles.videoControlsContainer}>
            {showReplayButton && videoFinished && (
              <TakeoverReplayButton
                onReplay={() => {
                  setVideoFinished(false);
                  videoReplayRef.current?.();
                }}
              />
            )}
            {videoHasAudio && (
              <Pressable
                style={styles.audioControlButton}
                onPress={() => {
                  const newMuted = !isMuted;

                  logButtonClick({
                    attributes: {
                      component: ClickEventComponentNames.BUTTON,
                      name: `${newMuted ? 'Mute' : 'Unmute'} SHP audio`,
                    },
                  });

                  setIsMuted(newMuted);
                  videoMuteRef.current?.(newMuted);
                }}
                accessibilityRole="button"
                accessibilityLabel={isMuted ? 'Unmute video' : 'Mute video'}
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Icon
                  variant={isMuted ? 'volumeOff' : 'volumeOn'}
                  size="$5"
                  color={isMuted ? primitive.$fieryRed : primitive.$bbqBrown}
                />
              </Pressable>
            )}
          </View>
        )}
      </View>
      <FooterContainer>
        <CTAButtons
          numberOfItemsInCart={numberOfItemsInCart}
          serviceMode={serviceMode}
          itemId={_id}
        />
      </FooterContainer>
    </View>
  );
};

// keep video component separate to avoid any native video stuff unless its active due to API design
const Video = ({
  videoUri,
  onLayout,
  onLoaded,
  safeAreaStyle,
  onVideoEnd,
  onReplayRef,
  onMuteRef,
}: {
  videoUri: string;
  onLayout: (event: LayoutChangeEvent) => void;
  onLoaded: () => void;
  safeAreaStyle: StyleProp<ViewStyle>;
  onVideoEnd: () => void;
  onReplayRef: React.RefObject<(() => void) | null>;
  onMuteRef: React.RefObject<((newMuted: boolean) => void) | null>;
}) => {
  const [thumbnail, setThumbnail] = useState<VideoThumbnail | null>(null);
  const isVideoEndedRef = useRef<boolean>(false);
  const [splashHidden, setSplashHidden] = useState(isWeb);

  const player = useVideoPlayer({ uri: videoUri }, player => {
    player.loop = false;
    player.muted = true;
  });

  // Expose replay and mute functions to parent
  useEffect(() => {
    onReplayRef.current = () => {
      isVideoEndedRef.current = false;
      setThumbnail(null);
      player.currentTime = 0;
      player.play();
    };
    onMuteRef.current = (newMuted: boolean) => {
      player.muted = newMuted;
    };
    return () => {
      onReplayRef.current = null;
      onMuteRef.current = null;
    };
  }, [player, onReplayRef, onMuteRef]);

  // NATIVE ONLY: wait for splash to hide before playing video (and showing the element, as it sometimes shows over the splash on android)
  useEffect(() => {
    if (isWeb) {
      return;
    }
    let didUnmount = false;

    animatedSplashScreenHiddenDeferred.promise.then(() => {
      if (didUnmount) {
        return;
      }
      setSplashHidden(true);
      player.play();
    });
    return () => {
      didUnmount = true;
    };
  }, []);

  // Ensure video plays to completion if app is brought back to focus during playback
  const {
    addAppStateChangeListeners: onFocusListener,
    removeAppStateChangeListeners: onFocusRemoveListener,
  } = useAppStateChangeListener();
  useEffect(() => {
    onFocusListener(() => {
      if (!isVideoEndedRef.current) {
        animatedSplashScreenHiddenDeferred.promise.then(() => {
          player.play();
        });
      }
    });
    return () => {
      onFocusRemoveListener();
    };
  }, [onFocusListener, onFocusRemoveListener, player]);

  useEffect(() => {
    const sub = player.addListener('playToEnd', () => {
      onVideoEnd();
      isVideoEndedRef.current = true;
      if (isWeb) {
        return;
      }
      // NATIVE only: generate a thumbnail of the last frame of the video because under various scrolling and navigation conditions:
      // - android video goes black and never comes back
      // - ios video gets stuck in various spots or beginning
      const duration = player.duration;
      if (!duration || !isFinite(duration)) {
        return;
      }
      player
        .generateThumbnailsAsync([duration])
        .then(thumbnails => {
          if (thumbnails[0]) {
            setThumbnail(thumbnails[0]);
          }
        })
        .catch(error => {
          logger.warn({ error, message: 'Error generating thumbnail for SHP video' });
        });
    });
    return () => sub.remove();
  }, [player, onVideoEnd]);

  // WEB only: requires a timeout on auto play due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
  useEffect(() => {
    if (!isWeb) {
      return;
    }
    const timeout = setTimeout(() => {
      player.play();
    }, 100);
    return () => clearTimeout(timeout);
  }, [player]);

  // don't show videoView until splash screen is hidden to prevent it showing over the splash (android has issues sometimes)
  if (!splashHidden) {
    return null;
  }

  return (
    <>
      {/*
      - pointerEvents="none" prevents VideoView's SurfaceView from swallowing touches on Android
      - remove video when done to reduce memory usage on ios, not supported on android it breaks things.
      */}
      {(!thumbnail || isAndroid) && (
        <View pointerEvents="none" style={[styles.video, safeAreaStyle]} onLayout={onLayout}>
          <VideoView
            style={styles.fill}
            onFirstFrameRender={onLoaded}
            contentFit="cover"
            nativeControls={false}
            allowsPictureInPicture={false}
            // @ts-ignore - VideoPlayer type mismatch between native and web
            player={player}
            playsInline
            crossOrigin="anonymous"
          />
        </View>
      )}
      {/** show over video view after complete to avoid painting issues, reduce memory usage*/}
      {thumbnail && (
        <Image source={thumbnail} style={StyleSheet.absoluteFill as any} contentFit="cover" />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  heroContainer: {
    flex: 1,
    position: 'relative',
  },
  pressable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    flex: 1,
  },
  lottie: {
    height: '100%',
    width: '100%',
  },
  video: {
    flex: 1,
    height: '100%',
    aspectRatio: 1.5,
  },
  videoControlsContainer: {
    position: 'absolute',
    bottom: spacing.$4,
    right: spacing.$4,
    flexDirection: 'row',
    gap: spacing.$2,
    alignItems: 'center',
  },
  audioControlButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: primitive.$mayoEggWhite,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: primitive.$bbqBrown,
  },
  fill: {
    flex: 1,
  },
});

const DisclaimerText = withTypography({
  fontSize: 9,
  mb: '$2',
  ml: '$5',
  style: { alignSelf: 'flex-start' },
});

const FooterContainer = Box.withConfig({
  paddingX: '$4',
  paddingY: '$4',
});

export const SimpleHomePageMobile = React.memo(_SimpleHomePageMobile);
